// Serbian (Latin and Cyrillic) dental vocabulary. All forms are folded (see
// fold.ts): "šesnaestica", "Шеснаестица" and "sesnaestica" all match.
//
// Teeth can be named as digits ("16", "16-ica"), as words ("šesnaestica",
// "dvadeset šest", "trideset i šest"), as FDI pairs ("jedan-šest",
// "tri šest") or by position ("gornja desna šestica", "donji levi umnjak").

import { isValidTooth, type Surface } from '@/utils/dental';
import type {
  CodeRule,
  Lexicon,
  LocationWord,
  MaterialRule,
  NumberRead,
  PhraseHit,
  Token,
} from './types';

// ── Numbers ──────────────────────────────────────────────────

/** Numerals used inside compound numbers ("dvadeset šest", "dvadeset devet"). */
const UNITS: Record<string, number> = {
  jedan: 1,
  jedna: 1,
  jedno: 1,
  dva: 2,
  dve: 2,
  tri: 3,
  cetiri: 4,
  pet: 5,
  sest: 6,
  sedam: 7,
  osam: 8,
  devet: 9,
};

/**
 * Digits of an FDI pair ("jedan-šest", "tri šest"). Only the plain numerals:
 * "dve šestice" / "jedna petica" are counts ("both sixes"), not tooth 26 / 15.
 */
const FDI_DIGITS: Record<string, number> = {
  jedan: 1,
  dva: 2,
  tri: 3,
  cetiri: 4,
  pet: 5,
  sest: 6,
  sedam: 7,
  osam: 8,
};

/** "šestica" = "the six": a position without a quadrant. Stems take case endings. */
const POSITION_STEMS: [string, number][] = [
  ['jedinic', 1],
  ['dvojk', 2],
  ['trojk', 3],
  ['cetvork', 4],
  ['petic', 5],
  ['sestic', 6],
  ['sedmic', 7],
  ['osmic', 8],
  ['umnjak', 8],
];

const TEENS: [string, number][] = [
  ['devetnaest', 19],
  ['sedamnaest', 17],
  ['osamnaest', 18],
  ['jedanaest', 11],
  ['cetrnaest', 14],
  ['dvanaest', 12],
  ['trinaest', 13],
  ['petnaest', 15],
  ['sesnaest', 16],
];

const TENS: [string, number][] = [
  ['sedamdeset', 70],
  ['osamdeset', 80],
  ['cetrdeset', 40],
  ['dvadeset', 20],
  ['trideset', 30],
  ['sezdeset', 60],
  ['pedeset', 50],
];

/** Endings after a number word: "šesnaest-ica", "šesnaest-og", "16-om". */
const SUFFIXES = new Set([
  '',
  'i',
  'a',
  'u',
  'e',
  'og',
  'om',
  'oj',
  'ica',
  'ice',
  'ici',
  'icu',
  'icom',
  'ka',
  'ke',
  'ki',
  'ku',
]);

function positionOfWord(norm: string): number | null {
  const hit = POSITION_STEMS.find(([stem]) => norm.startsWith(stem));
  return hit ? hit[1] : null;
}

/** Unit word or position noun after a tens word: "šest", "šestica". */
function unitOrPosition(norm: string): number | null {
  return UNITS[norm] ?? positionOfWord(norm);
}

function read(value: number, used: number, adjectival: boolean): NumberRead {
  return { value, used, adjectival, from: false, to: false };
}

/** "dvadeset šest", "dvadesetšestica", "trideset i šest". */
function readTens(tokens: Token[], i: number, base: number, rest: string): NumberRead | null {
  if (rest) {
    const unit = unitOrPosition(rest);
    return unit ? read(base + unit, 1, positionOfWord(rest) !== null) : null;
  }
  let j = i + 1;
  if (tokens[j]?.norm === 'i') j += 1;
  const next = tokens[j]?.norm;
  const unit = next ? unitOrPosition(next) : null;
  return unit ? read(base + unit, j - i + 1, positionOfWord(next ?? '') !== null) : null;
}

function readNumber(tokens: Token[], i: number): NumberRead | null {
  const norm = tokens[i].norm;
  const digits = /^(\d{2})(?:-?([a-z]+))?$/.exec(norm);
  if (digits) {
    const suffix = digits[2] ?? '';
    return SUFFIXES.has(suffix) ? read(Number(digits[1]), 1, suffix !== '') : null;
  }
  const teen = TEENS.find(([word]) => norm.startsWith(word));
  if (teen && SUFFIXES.has(norm.slice(teen[0].length))) {
    return read(teen[1], 1, norm.length > teen[0].length);
  }
  const tens = TENS.find(([word]) => norm.startsWith(word));
  return tens ? readTens(tokens, i, tens[1], norm.slice(tens[0].length)) : null;
}

/** "jedan-šest" → 16, "četiri sedam" → 47. */
function readQuadrantPair(a: string, b: string): number | null {
  const q = FDI_DIGITS[a];
  const p = FDI_DIGITS[b];
  if (!(q && p)) return null;
  const value = q * 10 + p;
  return isValidTooth(value) ? value : null;
}

const LOCATION_STEMS: [string, LocationWord][] = [
  ['gornj', 'upper'],
  ['donj', 'lower'],
  ['desn', 'right'],
  ['mlecn', 'primary'],
];

function location(norm: string): LocationWord | null {
  if (norm === 'gore') return 'upper';
  if (norm === 'dole') return 'lower';
  if (/^lev(i|a|o|e|og|om|oj|u)?$/.test(norm)) return 'left';
  const hit = LOCATION_STEMS.find(([stem]) => norm.startsWith(stem));
  if (hit) return hit[1];
  if (['strana', 'strani', 'strane', 'vilica', 'vilici', 'u', 'na'].includes(norm)) return 'filler';
  return null;
}

// ── Findings and procedures ──────────────────────────────────

// Order matters: the first rule that matches wins.
const CODE_RULES: CodeRule[] = [
  { code: 'root_canal', stems: ['endodont', 'endo', 'kanal', 'devitaliz'] },
  { code: 'periapical', stems: ['periapikal', 'periapical', 'granulom', 'apsc'] },
  { code: 'root_remnant', stems: ['radiks', 'radix'] },
  // "devitalan", "odumrla pulpa", "mrtav zub"
  {
    code: 'devital',
    stems: ['devital', 'avital', 'nekro', 'odumr'],
    exact: ['mrtav', 'mrtva', 'mrtvi'],
  },
  {
    code: 'caries',
    stems: ['karijes', 'karies', 'caries', 'kvar', 'pokvar', 'rupic'],
    exact: ['rupa', 'rupe', 'rupu', 'rupom'],
  },
  { code: 'sealant', stems: ['zaliv', 'silant', 'sealant'] },
  { code: 'inlay', stems: ['inlej', 'onlej', 'inlay', 'onlay'] },
  { code: 'filling', stems: ['plomb', 'ispun', 'restaur'] },
  {
    code: 'fracture',
    stems: ['fraktur', 'slomlj', 'polomlj', 'napuk', 'pukotin', 'okrnj', 'odlom', 'otkrhn'],
  },
  { code: 'bridge_pontic', stems: ['medjuclan', 'pontik', 'pontic'] },
  { code: 'bridge_abutment', stems: ['nosac'] },
  {
    code: 'bridge_abutment',
    exact: ['most', 'mosta', 'mostu', 'mostom', 'mostovi'],
    genericBridge: true,
  },
  { code: 'crown', stems: ['krunic', 'kruna', 'krune', 'krunu', 'krunom'] },
  { code: 'veneer', stems: ['ljusk', 'faset', 'viner', 'veneer'] },
  { code: 'extraction', stems: ['vadj', 'ekstrakc', 'ekstrah', 'izvad', 'izvuc'] },
  {
    code: 'missing',
    // "ispao", "ispala", "izbijen" (knocked out; not "izbija", which is erupting)
    stems: ['nedosta', 'izgubljen', 'odsut', 'ispao', 'ispal', 'ispad', 'izbijen'],
    exact: ['fali'],
  },
  { code: 'implant', stems: ['implant'] },
  {
    code: 'mobility',
    stems: ['klimav', 'klimaj', 'labav', 'pokretlj', 'pokretan', 'mobil', 'rasklim'],
    exact: ['klima'],
  },
  { code: 'calculus', stems: ['kamen'] },
  { code: 'impacted', stems: ['impakt', 'retinir', 'zadrzan'] },
  { code: 'wear', stems: ['abraz', 'erozij', 'istros', 'atric', 'abfrakc'] },
  { code: 'sound', stems: ['intakt'], exact: ['zdrav', 'zdrava', 'zdravo'] },
];

const SURFACE_STEMS: [string, Surface][] = [
  ['mezi', 'M'],
  ['mesi', 'M'],
  ['dist', 'D'],
  ['okluz', 'O'],
  ['oklu', 'O'],
  ['zvacn', 'O'],
  ['inciz', 'I'],
  ['incis', 'I'],
  ['bukal', 'B'],
  ['vestib', 'B'],
  ['labij', 'B'],
  ['facij', 'B'],
  ['lingv', 'L'],
  ['palat', 'L'],
  ['oral', 'L'],
];

const MATERIAL_RULES: MaterialRule[] = [
  { material: 'metal_ceramic', stems: ['metalokeram', 'metalkeram'] },
  { material: 'lithium_disilicate', stems: ['emax', 'e-max', 'litijum', 'disilikat'] },
  { material: 'zirconia', stems: ['cirkon'] },
  { material: 'glass_ionomer', stems: ['glasjonomer', 'staklenojonomer', 'jonomer', 'gic'] },
  { material: 'composite', stems: ['kompozit'] },
  { material: 'amalgam', stems: ['amalgam'] },
  { material: 'ceramic', stems: ['keram', 'porcelan'] },
  { material: 'gold', stems: ['zlat'] },
  { material: 'titanium', stems: ['titan'] },
  { material: 'temporary', stems: ['privremen', 'provizor'] },
];

const NOTE_STEMS: [string, string][] = [
  ['sekundar', 'sekundarni'],
  ['dubok', 'duboki'],
  ['pocetn', 'početni'],
  ['povrsinsk', 'površinski'],
  ['neadekvat', 'neadekvatan'],
  ['nepotpun', 'nepotpun'],
];

const FILLER_WORDS = new Set([
  // "zubni kamenac" (calculus): the adjective must not end a pending "nema/bez".
  'zubni',
  'zubnog',
  'zubnom',
  'zubnim',
  'zubna',
  'zubne',
  'zub',
  'zuba',
  'zubu',
  'zubom',
  'zubi',
  'zube',
  'zubima',
  'na',
  'je',
  'su',
  'i',
  'a',
  'se',
  'sa',
  'ima',
  'imamo',
  'broj',
  'povrsina',
  'povrsini',
  'povrsine',
  'strana',
  'strani',
  'ovaj',
  'ovom',
  'kod',
  'u',
  'takodje',
  'isto',
  'tu',
  'vidi',
  'vidljiv',
  'vidljivo',
  'prisutan',
  'prisutna',
  'prisutno',
  'postoji',
  'novi',
  'nova',
  'novo',
  'regija',
  'regiji',
  'vilica',
  'vilici',
  'od',
  'do',
]);

/** Two-word phrases: [first word, second word, what they mean]. */
const PHRASES: [RegExp, RegExp, PhraseHit][] = [
  [/^metalo?$/, /^keram/, { kind: 'material', material: 'metal_ceramic', used: 2 }],
  // "zaostali koren", "koren zaostao", "ostatak korena"
  [/^zaostal/, /^koren/, { kind: 'code', code: 'root_remnant', used: 2 }],
  [/^koren/, /^zaosta/, { kind: 'code', code: 'root_remnant', used: 2 }],
  [/^ostatak$/, /^koren/, { kind: 'code', code: 'root_remnant', used: 2 }],
  [/^telo$/, /^most/, { kind: 'code', code: 'bridge_pontic', used: 2 }],
];

/** "nema" alone means the tooth is missing; "nema karijesa" is a negation. */
function readPhrase(tokens: Token[], i: number): PhraseHit | null {
  const n = tokens[i].norm;
  const next = tokens[i + 1]?.norm;
  const phrase = next === undefined ? null : PHRASES.find(([a, b]) => a.test(n) && b.test(next));
  if (phrase) return phrase[2];
  if (n !== 'nema') return null;
  // "nema", "nema zuba", "nema ga"
  if (next === undefined || next === 'zuba' || next === 'zub' || next === 'ga') {
    return { kind: 'code', code: 'missing', used: next === undefined ? 1 : 2 };
  }
  // "nema 46", "nema šesnaestice", "nema gornje šestice": the tooth is missing.
  // "nema karijesa" stays a negation (handled by the engine).
  const place = location(next);
  const toothFollows =
    readNumber(tokens, i + 1) !== null ||
    positionOfWord(next) !== null ||
    (place !== null && place !== 'filler');
  return toothFollows ? { kind: 'code', code: 'missing', used: 1 } : null;
}

const GRADES: [string, string][] = [
  ['prv', 'I'],
  ['drug', 'II'],
  ['trec', 'III'],
  ['iii', 'III'],
  ['ii', 'II'],
  ['i', 'I'],
  ['1', 'I'],
  ['2', 'II'],
  ['3', 'III'],
];

/** "drugi stepen", "II stepena", "pokretljivost prvog stepena". */
function readGrade(tokens: Token[], i: number): { grade: string; used: number } | null {
  if (!tokens[i + 1]?.norm.startsWith('stepen')) return null;
  const word = tokens[i].norm;
  const hit = GRADES.find(([k]) => word === k || (k.length > 2 && word.startsWith(k)));
  return hit ? { grade: hit[1], used: 2 } : null;
}

export const sr: Lexicon = {
  lang: 'sr',
  readNumber,
  readQuadrantPair,
  readPosition: positionOfWord,
  location,
  rangeFrom: new Set(['od']),
  rangeTo: new Set(['do']),
  readPhrase,
  codeRules: CODE_RULES,
  surfaceStems: SURFACE_STEMS,
  // "do" and "od" are prepositions in Serbian, so they never mean surfaces here.
  lowercaseSurfaceAbbr: new Set(['mod', 'mo', 'om', 'modb', 'modl']),
  // A lone capital "I" is the word "and" at the start of a sentence, not "incisal".
  excludedSurfaceAbbr: new Set(['I']),
  materialRules: MATERIAL_RULES,
  isPlanned: (n) =>
    ['planir', 'potreb', 'treba', 'indikov', 'predvidj', 'zakaz'].some((st) => n.startsWith(st)),
  note: (n) => {
    if (['stari', 'stara', 'staru', 'staro', 'starog'].includes(n)) return 'stari';
    return NOTE_STEMS.find(([stem]) => n.startsWith(stem))?.[1] ?? null;
  },
  negationWords: new Set(['ne', 'nema', 'nije', 'nisu', 'bez']),
  correctionWords: new Set(['nego', 'vec']),
  fillerWords: FILLER_WORDS,
  separatorWords: new Set(['zatim', 'onda', 'potom', 'sledeci', 'dalje']),
  commandPatterns: [
    { command: 'approve_all', pattern: /^(sve )?(odobravam|odobri|potvrdjujem|potvrdi)( sve)?$/ },
    {
      command: 'undo',
      pattern: /^(ponisti|ponistavam|opozovi|obrisi poslednje|izbrisi poslednje)$/,
    },
    { command: 'yes', pattern: /^(da|moze)$/ },
    { command: 'no', pattern: /^ne$/ },
  ],
  noChangePattern: /(bez promena|nepromenjen|ostalo (je )?isto|sve ostalo (je )?u redu)/,
  price: { unit: 'din(?:ar[aeiu]?)?|rsd', thousand: 'hiljad[aeu]' },
  readGrade,
};
